/**
 * Streaming CSV tokenizer that reports the absolute byte offset of every record.
 *
 * Port of server/csv.js with Node Buffers replaced by Uint8Array + TextDecoder.
 * Those offsets are what make huge files workable: we index them once and then
 * seek straight to any row instead of re-reading the file from the top.
 *
 * Fields are decoded lazily. A filter usually looks at one or two columns, so
 * building strings for every cell of every row is the main cost worth avoiding.
 */

const CR = 0x0d
const LF = 0x0a
const BOM = [0xef, 0xbb, 0xbf]

const decoder = new TextDecoder('utf-8')

/**
 * Decode a byte range to a string. Short ASCII runs — the overwhelming case in
 * lead data — are built by hand, which beats a TextDecoder call per cell.
 */
export function decodeBytes(buf, start, end) {
  const len = end - start
  if (len === 0) return ''
  if (len <= 32) {
    let ascii = true
    for (let i = start; i < end; i++) {
      if (buf[i] >= 0x80) {
        ascii = false
        break
      }
    }
    if (ascii) {
      let s = ''
      for (let i = start; i < end; i++) s += String.fromCharCode(buf[i])
      return s
    }
  }
  return decoder.decode(buf.subarray(start, end))
}

/**
 * A mutable view over the record currently being emitted. Reused across records
 * to keep allocation down, so it is only valid inside the onRecord callback.
 * Call toArray() / get() there; do not stash the cursor itself.
 */
export class RecordCursor {
  constructor() {
    this.buf = null
    this.bufBase = 0 // absolute offset of buf[0]
    this.starts = []
    this.ends = []
    this.escaped = []
    this.quoted = []
    this.count = 0
    this.startOffset = 0 // absolute offset of the first byte of this record
    this.endOffset = 0 // absolute offset just past the record terminator
    this.index = -1 // 0-based record number emitted by this tokenizer
  }

  /** Decoded value of field i ('' when the column is missing from this row). */
  get(i) {
    if (i < 0 || i >= this.count) return ''
    const s = decodeBytes(this.buf, this.starts[i], this.ends[i])
    return this.escaped[i] ? s.replaceAll('""', '"') : s
  }

  toArray() {
    const out = new Array(this.count)
    for (let i = 0; i < this.count; i++) out[i] = this.get(i)
    return out
  }

  /**
   * How many fields hold anything at all, straight off the byte offsets — no
   * string is built. `""` is empty by content, which is what a reader means by it.
   */
  filled() {
    let n = 0
    for (let i = 0; i < this.count; i++) if (this.ends[i] > this.starts[i]) n++
    return n
  }

  /** Raw bytes of the record including its terminator — used for fast export. */
  rawBytes() {
    return this.buf.subarray(this.startOffset - this.bufBase, this.endOffset - this.bufBase)
  }
}

export class CsvTokenizer {
  /**
   * @param {object} opts
   * @param {string|number} opts.delimiter single-byte field delimiter
   * @param {string|number} [opts.quote]
   * @param {(cursor: RecordCursor) => void|false} opts.onRecord return false to stop
   * @param {number} [opts.startOffset] absolute offset the first written byte sits at
   * @param {boolean} [opts.skipEmptyLines]
   */
  constructor({ delimiter = ',', quote = '"', onRecord, startOffset = 0, skipEmptyLines = true }) {
    this.delim = typeof delimiter === 'number' ? delimiter : delimiter.charCodeAt(0)
    this.quote = typeof quote === 'number' ? quote : quote.charCodeAt(0)
    this.onRecord = onRecord
    this.skipEmptyLines = skipEmptyLines
    this.cursor = new RecordCursor()
    this.rowIndex = -1
    this.stopped = false
    this.malformedTail = false
    this.carry = null // bytes of an incomplete record, copied so callers can reuse buffers
    this.carryAbs = startOffset
    this.nextAbs = startOffset
  }

  write(chunk) {
    if (this.stopped || chunk.length === 0) return
    let buf, base
    if (this.carry !== null) {
      buf = new Uint8Array(this.carry.length + chunk.length)
      buf.set(this.carry, 0)
      buf.set(chunk, this.carry.length)
      base = this.carryAbs
    } else {
      buf = chunk
      base = this.nextAbs
    }
    this.nextAbs = base + buf.length

    const n = buf.length
    let pos = 0
    while (pos < n && !this.stopped) {
      const next = this.#record(buf, pos, n, base)
      if (next < 0) break
      pos = next
    }

    if (pos < n) {
      // Copy: `chunk` may be a reused read buffer that is about to be overwritten.
      this.carry = buf.slice(pos)
      this.carryAbs = base + pos
    } else {
      this.carry = null
      this.carryAbs = base + n
    }
  }

  /** Flush a final record that had no trailing newline. */
  end() {
    if (this.stopped || this.carry === null || this.carry.length === 0) return
    const tail = this.carry
    const abs = this.carryAbs
    this.carry = null
    this.nextAbs = abs
    const padded = new Uint8Array(tail.length + 1)
    padded.set(tail, 0)
    padded[tail.length] = LF
    this.write(padded)
    if (this.carry !== null && this.carry.length > 0) this.malformedTail = true
    this.carry = null
  }

  stop() {
    this.stopped = true
  }

  /**
   * Parse one record starting at `start`.
   * @returns index just past the record, or -1 if the buffer holds only part of it.
   */
  #record(buf, start, n, base) {
    const c = this.cursor
    const delim = this.delim
    const quote = this.quote
    let i = start
    let count = 0

    for (;;) {
      if (i >= n) return -1
      let fs
      let fe
      let escaped = false
      let isQuoted = false

      if (buf[i] === quote) {
        isQuoted = true
        i++
        fs = i
        for (;;) {
          if (i >= n) return -1
          if (buf[i] === quote) {
            // Need the next byte to tell a doubled-quote escape from the close.
            if (i + 1 >= n) return -1
            if (buf[i + 1] === quote) {
              escaped = true
              i += 2
              continue
            }
            fe = i
            i++
            break
          }
          i++
        }
        // Tolerate stray bytes between the closing quote and the delimiter.
        while (i < n && buf[i] !== delim && buf[i] !== CR && buf[i] !== LF) i++
      } else {
        fs = i
        while (i < n && buf[i] !== delim && buf[i] !== CR && buf[i] !== LF) i++
        fe = i
      }

      if (i >= n) return -1
      c.starts[count] = fs
      c.ends[count] = fe
      c.escaped[count] = escaped
      c.quoted[count] = isQuoted
      count++

      const b = buf[i]
      if (b === delim) {
        i++
        continue
      }

      let end
      if (b === CR) {
        if (i + 1 >= n) return -1 // might be CRLF; wait for the next byte
        end = buf[i + 1] === LF ? i + 2 : i + 1
      } else {
        end = i + 1
      }

      // A blank line is noise in every real-world CSV, so drop it.
      if (this.skipEmptyLines && count === 1 && fs === fe && !isQuoted) return end

      c.buf = buf
      c.bufBase = base
      c.count = count
      c.startOffset = base + start
      c.endOffset = base + end
      this.rowIndex++
      c.index = this.rowIndex
      if (this.onRecord(c) === false) this.stopped = true
      return end
    }
  }
}

export function bomLength(buf) {
  return buf.length >= 3 && buf[0] === BOM[0] && buf[1] === BOM[1] && buf[2] === BOM[2] ? 3 : 0
}

const CANDIDATES = [',', ';', '\t', '|']

/** Pick the delimiter that appears most often outside quotes on the first line. */
export function sniffDelimiter(buf) {
  const counts = new Map(CANDIDATES.map((d) => [d.charCodeAt(0), 0]))
  let inQuotes = false
  for (let i = 0; i < buf.length; i++) {
    const b = buf[i]
    if (b === 0x22) {
      if (inQuotes && buf[i + 1] === 0x22) i++
      else inQuotes = !inQuotes
      continue
    }
    if (inQuotes) continue
    if (b === LF || b === CR) break
    if (counts.has(b)) counts.set(b, counts.get(b) + 1)
  }
  let best = ','
  let bestCount = 0
  for (const [code, count] of counts) {
    if (count > bestCount) {
      bestCount = count
      best = String.fromCharCode(code)
    }
  }
  return best
}

/** Serialize one row back to CSV text (used for filtered exports of a column subset). */
export function serializeRow(values, delimiter) {
  let out = ''
  for (let i = 0; i < values.length; i++) {
    const v = values[i] ?? ''
    if (i > 0) out += delimiter
    out +=
      v.includes(delimiter) || v.includes('"') || v.includes('\n') || v.includes('\r')
        ? `"${v.replaceAll('"', '""')}"`
        : v
  }
  return out + '\n'
}
